// Hosted-agent helpers shared by the admin components (HERMES Fas 5).
// Mirrors backend validation in services/hosted_agents/models.py.

import type { HostedAgentRoutineFormState } from "./hosted-agent-routines"
import type { HostedAgentRuntimeLimitInputs } from "./hosted-agent-runtime-limits"
import type {
  HostedAgent,
  HostedAgentBudgetStatus,
  HostedAgentBudgetWindow,
  HostedAgentMemoryMode,
  HostedAgentReasoningEffort,
} from "@/api/admin-api"

import { toHostedAgentUpdatePayload } from "@/api/admin-api"

import {
  parseHostedAgentRoutines,
  toHostedAgentRoutineDoc,
  validateHostedAgentRoutine,
} from "./hosted-agent-routines"
import {
  applyHostedAgentRuntimeLimits,
  invalidHostedAgentRuntimeLimits,
  readHostedAgentRuntimeLimits,
} from "./hosted-agent-runtime-limits"

export {
  HOSTED_AGENT_MAX_ITERATIONS,
  HOSTED_AGENT_MAX_RUN_SECONDS,
  HOSTED_AGENT_MAX_WEB_TOOL_CALLS,
  parseHostedAgentMaxIterations,
  parseHostedAgentRuntimeLimit,
} from "./hosted-agent-runtime-limits"

export type { HostedAgentRoutineFormState } from "./hosted-agent-routines"

/** Backend SLUG_PATTERN: lowercase alnum, optional interior hyphens. */
export const HOSTED_AGENT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

/** Max slug length (used in container names). */
export const HOSTED_AGENT_SLUG_MAX_LENGTH = 63

/** Reserved slugs — "templates" collides with the template catalog path. */
export const HOSTED_AGENT_RESERVED_SLUGS = new Set(["templates"])

export type HostedAgentSlugError = "required" | "invalid" | "reserved" | "too_long"

/** Validate a slug client-side; returns null when valid. */
export function validateHostedAgentSlug(slug: string): HostedAgentSlugError | null {
  const trimmed = slug.trim()
  if (!trimmed) return "required"
  if (trimmed.length > HOSTED_AGENT_SLUG_MAX_LENGTH) return "too_long"
  if (!HOSTED_AGENT_SLUG_PATTERN.test(trimmed)) return "invalid"
  if (HOSTED_AGENT_RESERVED_SLUGS.has(trimmed)) return "reserved"
  return null
}

/** Derive a slug suggestion from a display name (best-effort, may be empty). */
export function suggestHostedAgentSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, HOSTED_AGENT_SLUG_MAX_LENGTH)
}

// =============================================================================
// Edit wizard (Fas 5) \u2014 form state, defaults and validation.
// =============================================================================

/** Backend field limits (services/hosted_agents/models.py). */
export const HOSTED_AGENT_NAME_MAX_LENGTH = 120
export const HOSTED_AGENT_TITLE_MAX_LENGTH = 200
export const HOSTED_AGENT_DESCRIPTION_MAX_LENGTH = 4000
export const HOSTED_AGENT_INITIALS_MAX_LENGTH = 3
export const HOSTED_AGENT_AVATAR_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/

/** All reasoning efforts accepted by AgentModelConfig. */
export const HOSTED_AGENT_REASONING_EFFORTS: HostedAgentReasoningEffort[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]

/**
 * Skill-pack catalog (MVP): the runtime skill registry
 * (services/skills/registry.py::RUNTIME_SKILL_NAMES) has no admin list
 * endpoint yet, so the known ids are mirrored here.
 */
export const HOSTED_AGENT_SKILL_IDS = [
  "pdf",
  "docx",
  "pptx",
  "xlsx",
  "internal-comms",
  "doc-coauthoring",
] as const

/**
 * Prefilled budget defaults (HERMES_PLAN risk 5): the budget fields are
 * REQUIRED in the admin UI so a runaway agent can never spend without
 * bounds, even though the backend model technically allows null.
 */
export const HOSTED_AGENT_DEFAULT_BUDGET = {
  daily_tokens: 1_000_000,
  monthly_tokens: 20_000_000,
  monthly_sek: 1_000,
} as const

/**
 * `budget.alert_threshold_percent` bounds (#3039) — mirrors AgentBudget:
 * default 80, ge=1, le=99 (the 100 % line is the `budget_exceeded` alert).
 */
export const HOSTED_AGENT_ALERT_THRESHOLD = { default: 80, min: 1, max: 99 } as const

/** Editable state behind the wizard tabs \u2014 numbers kept as input strings. */
export type HostedAgentFormState = HostedAgentRuntimeLimitInputs & {
  enabled: boolean
  name: string
  title: string
  description: string
  introduction: string
  starterPrompts: string[]
  avatarCharacter: NonNullable<HostedAgent["profile"]["avatar"]["character"]> | ""
  avatarColor: string
  avatarInitials: string
  instructions: string
  modelSelection: "auto" | "fixed"
  modelId: string
  reasoningEffort: HostedAgentReasoningEffort
  tools: string[]
  /** Per-tool numeric overrides; unknown saved keys are preserved. */
  toolOverrides: Record<string, unknown>
  /**
   * A3.1 per-action tool permissions (`tool_actions`): tool name → allowlist
   * of permitted action names. An empty/absent allowlist means "all actions
   * allowed". The Tools tab edits this for selected tools that expose actions.
   */
  toolActions: Record<string, string[]>
  /** Explicit library grants validated server-side. */
  libraries: string[]
  libraryContexts: Record<string, string>
  skills: string[]
  memoryMode: HostedAgentMemoryMode
  budgetDailyTokens: string
  budgetMonthlyTokens: string
  budgetMonthlySek: string
  budgetOnExceeded: "block" | "budget_model"
  /** `budget.alert_threshold_percent` as an input string (#3039). */
  budgetAlertThresholdPercent: string
  /** Scheduled routines retain their server bookkeeping. */
  routines: HostedAgentRoutineFormState[]
}

export type HostedAgentFormField =
  | "name"
  | "avatarColor"
  | "modelId"
  | "budgetDailyTokens"
  | "budgetMonthlyTokens"
  | "budgetMonthlySek"
  | "budgetAlertThresholdPercent"
  | "runtimeMaxIterations"
  | "runtimeMaxWebToolCalls"
  | "runtimeMaxRunSeconds"
  | "routines"

export type HostedAgentFormErrorCode =
  | "required"
  | "invalid_color"
  | "invalid_number"
  | "invalid_routine"

export type HostedAgentFormErrors = Partial<Record<HostedAgentFormField, HostedAgentFormErrorCode>>

export type HostedAgentWizardTab =
  | "profile"
  | "instructions"
  | "model"
  | "tools"
  | "libraries"
  | "skills"
  | "memory"
  | "budget"
  | "routines"
  | "access"
  | "test"
  | "evals"
  | "lifecycle"
  | "raw"
  | "sources"
  | "publish"

/** Initialize the form from a freshly read agent (budget nulls \u2192 defaults). */
export function buildHostedAgentFormState(agent: HostedAgent): HostedAgentFormState {
  return {
    enabled: agent.enabled,
    name: agent.profile.name,
    title: agent.profile.title,
    description: agent.profile.description,
    introduction: agent.profile.introduction ?? "",
    starterPrompts: [...(agent.profile.starter_prompts ?? [])],
    avatarCharacter: agent.profile.avatar.character ?? "",
    avatarColor: agent.profile.avatar.color || "#6366f1",
    avatarInitials: agent.profile.avatar.initials,
    instructions: agent.instructions,
    modelSelection: agent.model.selection === "fixed" ? "fixed" : "auto",
    modelId: agent.model.model_id ?? "",
    reasoningEffort: agent.model.reasoning_effort,
    tools: [...agent.tools],
    toolOverrides: { ...agent.tool_overrides },
    toolActions: Object.fromEntries(
      Object.entries(agent.tool_actions).map(([tool, actions]) => [tool, [...actions]])
    ),
    libraries: [...agent.libraries],
    libraryContexts: { ...agent.library_contexts },
    skills: [...agent.skills],
    memoryMode: agent.memory_mode,
    budgetDailyTokens: String(
      agent.budget.daily_tokens ?? HOSTED_AGENT_DEFAULT_BUDGET.daily_tokens
    ),
    budgetMonthlyTokens: String(
      agent.budget.monthly_tokens ?? HOSTED_AGENT_DEFAULT_BUDGET.monthly_tokens
    ),
    budgetMonthlySek: String(agent.budget.monthly_sek ?? HOSTED_AGENT_DEFAULT_BUDGET.monthly_sek),
    budgetOnExceeded: agent.budget.on_exceeded,
    budgetAlertThresholdPercent: String(
      agent.budget.alert_threshold_percent ?? HOSTED_AGENT_ALERT_THRESHOLD.default
    ),
    // runtime limits (#3033, #3334); mem_limit/pids_limit stay hidden.
    ...readHostedAgentRuntimeLimits(agent.runtime),
    routines: parseHostedAgentRoutines(agent.routines),
  }
}

/** Parse the alert-threshold input: an integer within [min, max], else null. */
export function parseHostedAgentAlertThreshold(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const normalized = trimmed.replace(/\s+/g, "")
  const numeric = normalized.endsWith("%") ? normalized.slice(0, -1) : normalized
  const parsed = Number(numeric)
  if (!Number.isInteger(parsed)) return null
  if (parsed < HOSTED_AGENT_ALERT_THRESHOLD.min || parsed > HOSTED_AGENT_ALERT_THRESHOLD.max) {
    return null
  }
  return parsed
}

/** Parse a required non-negative budget number; null when invalid. */
export function parseHostedAgentBudgetNumber(value: string, integer: boolean): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed.replace(/\s+/g, "").replace(",", "."))
  if (!Number.isFinite(parsed) || parsed < 0) return null
  if (integer && !Number.isInteger(parsed)) return null
  return parsed
}

/** Validate the whole form; an empty result means "save away". */
export function validateHostedAgentForm(form: HostedAgentFormState): HostedAgentFormErrors {
  const errors: HostedAgentFormErrors = {}
  if (!form.name.trim()) errors.name = "required"
  if (!HOSTED_AGENT_AVATAR_COLOR_PATTERN.test(form.avatarColor.trim())) {
    errors.avatarColor = "invalid_color"
  }
  if (form.modelSelection === "fixed" && !form.modelId.trim()) errors.modelId = "required"
  if (parseHostedAgentBudgetNumber(form.budgetDailyTokens, true) === null) {
    errors.budgetDailyTokens = "invalid_number"
  }
  if (parseHostedAgentBudgetNumber(form.budgetMonthlyTokens, true) === null) {
    errors.budgetMonthlyTokens = "invalid_number"
  }
  if (parseHostedAgentBudgetNumber(form.budgetMonthlySek, false) === null) {
    errors.budgetMonthlySek = "invalid_number"
  }
  if (parseHostedAgentAlertThreshold(form.budgetAlertThresholdPercent) === null) {
    errors.budgetAlertThresholdPercent = "invalid_number"
  }
  for (const field of invalidHostedAgentRuntimeLimits(form)) errors[field] = "invalid_number"
  if (form.routines.some((routine) => validateHostedAgentRoutine(routine).length > 0)) {
    errors.routines = "invalid_routine"
  }
  return errors
}

/** The wizard tab that owns the first invalid field, for focus-on-error. */
export function firstInvalidHostedAgentTab(
  errors: HostedAgentFormErrors
): HostedAgentWizardTab | null {
  if (errors.name || errors.avatarColor) return "profile"
  if (
    errors.modelId ||
    errors.runtimeMaxIterations ||
    errors.runtimeMaxWebToolCalls ||
    errors.runtimeMaxRunSeconds
  ) {
    return "model"
  }
  if (
    errors.budgetDailyTokens ||
    errors.budgetMonthlyTokens ||
    errors.budgetMonthlySek ||
    errors.budgetAlertThresholdPercent
  ) {
    return "budget"
  }
  if (errors.routines) return "routines"
  return null
}

/**
 * Build the PUT payload: carry every stored field forward from the latest
 * read (`toHostedAgentUpdatePayload`), then apply the form on top. Assumes
 * `validateHostedAgentForm` returned no errors. `tool_overrides` entries for
 * tools no longer in the list are stripped \u2014 the backend rejects orphans.
 */
export function buildHostedAgentSavePayload(
  agent: HostedAgent,
  form: HostedAgentFormState
): Record<string, unknown> {
  const payload = toHostedAgentUpdatePayload(agent)
  const selectedTools = new Set(form.tools)
  const overrides: Record<string, unknown> = {}
  for (const [tool, override] of Object.entries(form.toolOverrides)) {
    if (selectedTools.has(tool)) overrides[tool] = override
  }
  // A3.1 + #3030 punkt 1: carry only the allowlists for still-selected tools,
  // and DROP empty ones — the backend rejects an explicit empty allowlist on
  // write (absent = all actions allowed; [] = nothing allowed, fail-closed at
  // runtime for legacy documents), so an empty entry must never be sent.
  // Orphaned entries would be rejected by the model.
  const toolActions: Record<string, string[]> = {}
  for (const [tool, actions] of Object.entries(form.toolActions)) {
    if (selectedTools.has(tool) && actions.length > 0) toolActions[tool] = actions
  }
  payload.enabled = form.enabled
  payload.profile = {
    ...agent.profile,
    name: form.name.trim(),
    title: form.title.trim(),
    description: form.description,
    introduction: form.introduction.trim(),
    starter_prompts: form.starterPrompts.map((prompt) => prompt.trim()).filter(Boolean),
    avatar: {
      ...agent.profile.avatar,
      color: form.avatarColor.trim(),
      initials: form.avatarInitials.trim().slice(0, HOSTED_AGENT_INITIALS_MAX_LENGTH),
      character: form.avatarCharacter || null,
    },
  }
  payload.instructions = form.instructions
  payload.model =
    form.modelSelection === "auto"
      ? { selection: "auto", model_id: null, reasoning_effort: form.reasoningEffort }
      : { selection: "fixed", model_id: form.modelId, reasoning_effort: form.reasoningEffort }
  payload.tools = form.tools
  payload.tool_overrides = overrides
  payload.tool_actions = toolActions
  payload.libraries = form.libraries
  payload.library_contexts = Object.fromEntries(
    Object.entries(form.libraryContexts).filter(([id]) => form.libraries.includes(id))
  )
  payload.skills = form.skills
  payload.memory_mode = form.memoryMode
  payload.budget = {
    daily_tokens: parseHostedAgentBudgetNumber(form.budgetDailyTokens, true),
    monthly_tokens: parseHostedAgentBudgetNumber(form.budgetMonthlyTokens, true),
    monthly_sek: parseHostedAgentBudgetNumber(form.budgetMonthlySek, false),
    on_exceeded: form.budgetOnExceeded,
    alert_threshold_percent:
      parseHostedAgentAlertThreshold(form.budgetAlertThresholdPercent) ??
      HOSTED_AGENT_ALERT_THRESHOLD.default,
  }
  // runtime (#3033, #3334): the editable limits replace the stored ones;
  // everything else (mem_limit, pids_limit, unknown keys) rides along.
  payload.runtime = applyHostedAgentRuntimeLimits(agent.runtime, form)
  // routines (B1) are edited in the Routines tab and serialized here; links
  // (B4) stay hidden and ride along unchanged via toHostedAgentUpdatePayload.
  payload.routines = form.routines.map(toHostedAgentRoutineDoc)
  return payload
}

// =============================================================================
// Tool overrides (Tools tab) \u2014 the numeric limits the runtime consumes.
// =============================================================================

/** The tool_overrides fields the Tools tab edits as numbers. */
export type HostedAgentToolOverrideField = "max_tokens" | "max_calls_per_iteration"

/** Read a numeric override for a tool as an input string ("" = unset). */
export function getHostedAgentToolOverrideValue(
  overrides: Record<string, unknown>,
  tool: string,
  field: HostedAgentToolOverrideField
): string {
  const entry = overrides[tool]
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return ""
  const value = (entry as Record<string, unknown>)[field]
  return typeof value === "number" && Number.isFinite(value) ? String(value) : ""
}

/** True when a tool's override entry cannot be edited structurally (not an object). */
export function isHostedAgentToolOverrideOpaque(
  overrides: Record<string, unknown>,
  tool: string
): boolean {
  const entry = overrides[tool]
  return (
    entry !== undefined && (typeof entry !== "object" || entry === null || Array.isArray(entry))
  )
}

/**
 * Set/unset a numeric override for a tool, preserving unknown fields.
 * Empty/invalid input unsets the field; an entry with no fields left is
 * removed entirely. Returns a new overrides map.
 */
export function setHostedAgentToolOverrideValue(args: {
  overrides: Record<string, unknown>
  tool: string
  field: HostedAgentToolOverrideField
  raw: string
}): Record<string, unknown> {
  const { overrides, tool, field, raw } = args
  const current = overrides[tool]
  const entry: Record<string, unknown> =
    current && typeof current === "object" && !Array.isArray(current)
      ? { ...(current as Record<string, unknown>) }
      : {}
  const trimmed = raw.trim()
  const parsed = Number(trimmed)
  if (trimmed && Number.isInteger(parsed) && parsed > 0) {
    entry[field] = parsed
  } else {
    delete entry[field]
  }
  const next = { ...overrides }
  if (Object.keys(entry).length === 0) {
    delete next[tool]
  } else {
    next[tool] = entry
  }
  return next
}

// =============================================================================
// Tool picker grouping (#3022) — sections, bindings and fail-closed marking.
// =============================================================================

/**
 * Section ids for the Tools tab, in render order. They mirror the `group`
 * field the catalog endpoint derives from each tool's manifest tags; `other`
 * is the fallback for an unmapped (or missing) group.
 */
export const HOSTED_AGENT_TOOL_GROUPS = [
  "data",
  "dashboards",
  "knowledge",
  "web",
  "production",
  "integrations",
  "workspace",
  "other",
] as const

export type HostedAgentToolGroup = (typeof HOSTED_AGENT_TOOL_GROUPS)[number]

const HOSTED_AGENT_TOOL_GROUP_SET = new Set<string>(HOSTED_AGENT_TOOL_GROUPS)

/**
 * English fallbacks for the section headings in the tools tab; the shipped
 * strings live in `adminDashboard.hostedAgents.toolsTab.groups.*` in all five
 * locales.
 */
export const HOSTED_AGENT_TOOL_GROUP_LABELS: Record<HostedAgentToolGroup, string> = {
  data: "Data & SQL",
  dashboards: "Dashboards & bindings",
  knowledge: "Knowledge & search",
  web: "Web",
  production: "Documents & media",
  integrations: "External systems",
  workspace: "Workspace & automation",
  other: "Other",
}

/** Map a backend group id onto a known section ("other" when unknown/empty). */
export function normalizeHostedAgentToolGroup(group: string): HostedAgentToolGroup {
  const trimmed = group.trim()
  return HOSTED_AGENT_TOOL_GROUP_SET.has(trimmed) ? (trimmed as HostedAgentToolGroup) : "other"
}

/**
 * Tools that only produce results once something is bound to the agent in
 * another wizard tab. Value = the tab that carries the binding.
 */
export const HOSTED_AGENT_TOOL_BINDINGS: Record<string, "libraries" | undefined> = {
  library_search: "libraries",
}

/** Split catalog rows into render-ordered sections; empty sections are dropped. */
export function groupHostedAgentTools<T extends { name: string; group: string }>(
  tools: T[]
): { group: HostedAgentToolGroup; tools: T[] }[] {
  const buckets = new Map<HostedAgentToolGroup, T[]>()
  for (const tool of tools) {
    const group = normalizeHostedAgentToolGroup(tool.group)
    const bucket = buckets.get(group)
    if (bucket) bucket.push(tool)
    else buckets.set(group, [tool])
  }
  return HOSTED_AGENT_TOOL_GROUPS.filter((group) => (buckets.get(group)?.length ?? 0) > 0).map(
    (group) => ({
      group,
      tools: [...(buckets.get(group) ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    })
  )
}

/** Locale-aware "medium date, short time" formatting for ISO timestamps. */
export function formatHostedAgentTimestamp(
  iso: string | null | undefined,
  locale?: string
): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date)
  } catch {
    return date.toLocaleString()
  }
}

// =============================================================================
// Budget status (Fas 6)
// =============================================================================

export type HostedAgentBudgetSummary = {
  /** Highest utilization across the configured windows, in percent. */
  percent: number
  /** True when any window is exceeded (reservations included). */
  exceeded: boolean
}

/**
 * Collapse a `budget_status` payload to the worst configured window for the
 * table's percent chip. Returns null when the status is absent ("cannot
 * say" — quota service not wired / rollup read failed) or when no window
 * has a configured limit.
 */
export function summarizeHostedAgentBudget(
  status: HostedAgentBudgetStatus | null | undefined
): HostedAgentBudgetSummary | null {
  if (!status) return null
  const windows = [
    status.windows.daily_tokens,
    status.windows.monthly_tokens,
    status.windows.monthly_sek,
  ].filter((window): window is HostedAgentBudgetWindow => window !== null)
  if (windows.length === 0) return null
  return {
    percent: Math.max(...windows.map((window) => window.percent)),
    exceeded: status.exceeded,
  }
}
